// src/store/Slices/depositSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../API/api';

// عناويني
export const fetchMyDepositAddresses = createAsyncThunk(
  'deposit/fetchMyDepositAddresses',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/wallets/my-addresses', { timeout: 20000 });
      if (!Array.isArray(data)) return [];
      return data.map(x => ({
        asset_symbol: x.asset_symbol,
        network_name: x.network_name,
        address: x.address,
      }));
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message || 'Failed to fetch addresses');
    }
  }
);

// إنشاء Intent
export const createDepositIntent = createAsyncThunk(
  'deposit/createDepositIntent',
  async ({ asset_symbol, network_name, amount }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/wallets/deposit/intent', { asset_symbol, network_name, amount }, { timeout: 20000 });
      return data; // { intent_id, address, amount_to_send, memo_tag, expires_at }
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message || 'Failed to create deposit intent');
    }
  }
);

// قائمة Intents
export const fetchDepositIntents = createAsyncThunk(
  'deposit/fetchDepositIntents',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/wallets/deposit/intents', { timeout: 20000 });
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message || 'Failed to fetch deposit intents');
    }
  }
);

// سجل الإيداعات (آخر 100)
export const fetchDepositHistory = createAsyncThunk(
  'deposit/fetchDepositHistory',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/wallets/deposits/history', { timeout: 20000 });
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message || 'Failed to fetch deposit history');
    }
  }
);

const depositSlice = createSlice({
  name: 'deposit',
  initialState: {
    addresses: [],
    addressesLoading: false,

    intents: [],
    intentsLoading: false,

    history: [],
    historyLoading: false,

    creating: false,
    activeIntent: null,

    error: null,
  },
  reducers: {
    clearActiveIntent: (state) => {
      state.activeIntent = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // addresses
      .addCase(fetchMyDepositAddresses.pending, (state) => {
        state.addressesLoading = true; state.error = null;
      })
      .addCase(fetchMyDepositAddresses.fulfilled, (state, action) => {
        state.addressesLoading = false; state.addresses = action.payload || [];
      })
      .addCase(fetchMyDepositAddresses.rejected, (state, action) => {
        state.addressesLoading = false; state.error = action.payload || 'Failed to load addresses';
      })

      // intents
      .addCase(fetchDepositIntents.pending, (state) => {
        state.intentsLoading = true; state.error = null;
      })
      .addCase(fetchDepositIntents.fulfilled, (state, action) => {
        state.intentsLoading = false; state.intents = action.payload || [];
      })
      .addCase(fetchDepositIntents.rejected, (state, action) => {
        state.intentsLoading = false; state.error = action.payload || 'Failed to load intents';
      })

      // history
      .addCase(fetchDepositHistory.pending, (state) => {
        state.historyLoading = true; state.error = null;
      })
      .addCase(fetchDepositHistory.fulfilled, (state, action) => {
        state.historyLoading = false; state.history = action.payload || [];
      })
      .addCase(fetchDepositHistory.rejected, (state, action) => {
        state.historyLoading = false; state.error = action.payload || 'Failed to load history';
      })

      // create intent
      .addCase(createDepositIntent.pending, (state) => {
        state.creating = true; state.error = null;
      })
      .addCase(createDepositIntent.fulfilled, (state, action) => {
        state.creating = false; state.activeIntent = action.payload;
        // ادفع إلى أعلى قائمة intents كمرجع سريع للمستخدم
        state.intents = [{ 
          asset_symbol: action.meta?.arg?.asset_symbol,
          network_name: action.meta?.arg?.network_name,
          expected_amount: action.payload.amount_to_send,
          deposit_address: action.payload.address,
          memo_tag: action.payload.memo_tag,
          status: 'Pending',
          createdAt: new Date().toISOString()
        }, ...state.intents];
      })
      .addCase(createDepositIntent.rejected, (state, action) => {
        state.creating = false; state.error = action.payload || 'Failed to create intent';
      });
  },
});

export const { clearActiveIntent } = depositSlice.actions;
export default depositSlice.reducer;
